import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Search, Filter, SortAsc, SortDesc, Calendar, FileText, RotateCcw, ChevronDown, ChevronLeft, ChevronRight, Send, AlertCircle, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { api } from '@/contexts/AuthContext';
// ✅ AGREGAR: Import de CampaignBuilder
import CampaignBuilder from './CampaignBuilder';
import TemplatePreviewPanel from './TemplatePreviewPanel';
import {
  resolveTemplatePreview,
  formatTemplateName,
  buildTemplatesMap,
  type TemplateMapEntry,
} from '@/lib/templatePreview';

// Import types
import {
  CampaignListProps,
  CampaignListItem,
  CampaignFilters,
  CampaignStatus,
  SortOption,
  GoogleSheetWithCount,
  CampaignCardProps,
  CampaignFilterParams,
  StatusConfig,
} from '@/types';

// Tipo para mensajes de campaña
interface CampaignMessage {
  id: string;
  message_id?: string;
  phone_number: string;
  name?: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'skipped';
  error?: string;
  error_code?: number;
  sent_at?: string;
  delivered_at?: string;
  created_at?: string;
  row_index?: number;
  template_name?: string;
  rendered_message?: string;
  whatsapp_message_id?: string;
}

interface CampaignMessagesResponse {
  success: boolean;
  messages: CampaignMessage[];
  total: number;
  status_counts: Record<string, number>;
  pagination: {
    skip: number;
    limit: number;
    has_more: boolean;
  };
}

const CampaignList: React.FC<CampaignListProps> = ({ onCreateNew }) => {
  const [campaigns, setCampaigns] = useState<CampaignListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [googleSheets, setGoogleSheets] = useState<GoogleSheetWithCount[]>([]);
  const [templateCache, setTemplateCache] = useState<Record<string, TemplateMapEntry>>({});
  const [templatesMap, setTemplatesMap] = useState<Record<string, TemplateMapEntry>>({});
  const [detailsTemplateName, setDetailsTemplateName] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 6;

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sheetFilter, setSheetFilter] = useState<string>('');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState<boolean>(false);

  // Details modal state
  const [detailsCampaign, setDetailsCampaign] = useState<CampaignListItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);

  // ✅ NUEVO: Estado para mensajes de la campaña en el modal de detalles
  const [campaignMessages, setCampaignMessages] = useState<CampaignMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [messagesStatusCounts, setMessagesStatusCounts] = useState<Record<string, number>>({});
  const [messagesTotal, setMessagesTotal] = useState<number>(0);

  // ✅ NUEVO: Estado para ejecución de campaña
  const [executingCampaign, setExecutingCampaign] = useState<string | null>(null);

  // ✅ NUEVO: Estado para modal de edición
  const [editingCampaign, setEditingCampaign] = useState<CampaignListItem | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);

  // Delete confirmation modal state (soft-delete)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name?: string } | null>(null);

  const openDeleteConfirm = (campaign: CampaignListItem) => {
    setDeleteTarget({ id: campaign.id, name: campaign.name });
    setShowDeleteModal(true);
  };

  const closeDeleteConfirm = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  // ✅ NUEVO: Handlers para edición
  const openEditModal = async (campaign: CampaignListItem) => {
    console.log('📝 Abriendo editor para campaña:', campaign);

    // Enriquecer campaign con datos necesarios si faltan
    const enrichedCampaign = { ...campaign } as any;

    // Resolver nombre de Google Sheet si falta
    if (!enrichedCampaign.google_sheet_name && enrichedCampaign.google_sheet_id) {
      const sheetName = await resolveGoogleSheetName(enrichedCampaign.google_sheet_id);
      enrichedCampaign.google_sheet_name = sheetName;
    }

    // Resolver nombre de template si falta
    const templateId = enrichedCampaign.template_id;
    if (templateId && !enrichedCampaign.template_name) {
      const templateName = await resolveTemplateName(templateId);
      enrichedCampaign.template_name = templateName;
    }

    setEditingCampaign(enrichedCampaign);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingCampaign(null);
    // Recargar campañas después de editar
    loadCampaigns();
  };

  // Nombre resuelto de la Google Sheet para el modal
  const [detailsGoogleSheetName, setDetailsGoogleSheetName] = useState<string | null>(null);

  // Resuelve nombre de hoja: busca en state googleSheets o pide al backend /google-sheets/{id}
  const resolveGoogleSheetName = async (sheetId?: string | null) => {
    if (!sheetId) return null;

    // Buscar en estado local tratando varios posibles campos (id, sheet_id, _id, sheetId)
    const found = googleSheets.find((s: any) => {
      const candidates = [s.id, s.sheet_id, s._id, s.sheetId];
      return candidates.some(c => c != null && String(c) === String(sheetId));
    });
    if (found) {
      return (found.name ?? (found as any).title ?? (found as any).sheet_name ?? String(sheetId));
    }

    // Si no está en el state, intentar backend por id
    try {
      const res = await api.get<{ id?: string; sheet_id?: string; name?: string; title?: string; sheet_name?: string }>(`/google-sheets/${sheetId}`);
      const doc = res?.data;
      if (doc) {
        // resolver nombre por varios campos
        const name = doc.name ?? doc.title ?? doc.sheet_name ?? null;
        // añadir al estado para próximas búsquedas
        setGoogleSheets(prev => prev.some((s: any) => String(s.id || s.sheet_id || s._id) === String(doc.id ?? doc.sheet_id)) ? prev : [...prev, (doc as any)]);
        return name ?? String(sheetId);
      }
    } catch (err) {
      console.debug('No se pudo obtener nombre de google sheet desde backend:', err);
    }

    // fallback: devolver el id legible si no hay nombre
    return String(sheetId);
  };

  // Resuelve nombre de plantilla por id: primero mira templateCache, luego llama al backend /templates/{id}
  const resolveTemplateName = async (templateId?: string | null) => {
    if (!templateId) return null;
    // revisar cache local primero
    const cached = templateCache[String(templateId)];
    if (cached && cached.name) return cached.name;

    try {
      const res = await api.get<{ id?: string; name?: string; template?: string; message?: string; category?: string; header_type?: string; header_text?: string; header_image_url?: string; footer_text?: string; buttons?: any[] }>(`/templates/${templateId}`);
      const name = res?.data?.name ?? null;
      const message = res?.data?.message ?? res?.data?.template ?? undefined;
      const category = res?.data?.category ?? undefined;
      const header_type = res?.data?.header_type ?? undefined;
      const header_text = res?.data?.header_text ?? undefined;
      const header_image_url = res?.data?.header_image_url ?? undefined;
      const footer_text = res?.data?.footer_text ?? undefined;
      const buttons = res?.data?.buttons ?? undefined;

      // Si el nombre viene con guiones bajos, lo formateamos para el cache
      const prettyName = name ? formatTemplateName(name) : (res?.data?.name || null);

      // actualizar cache para futuras consultas
      setTemplateCache(prev => ({ 
        ...prev, 
        [String(templateId)]: { 
          ...(prev[String(templateId)] ?? {}), 
          name: prettyName ?? prev[String(templateId)]?.name, 
          message, 
          category,
          header_type,
          header_text,
          header_image_url,
          footer_text,
          buttons
        } 
      }));
      return prettyName ?? String(templateId);
    } catch (err) {
      console.debug('No se pudo obtener plantilla desde backend:', err);
      return String(templateId);
    }
  };

  // Handlers para abrir/cerrar modal — definir antes de CampaignCard para que sea accesible
  const openDetails = async (campaign: CampaignListItem) => {
    setDetailsCampaign(campaign);
    setShowDetailsModal(true);
    // resolver nombre de sheet (async). considerar varios campos que pueden guardar el id en la colección
    const anyc = campaign as any;
    const sheetId = anyc.google_sheet_id ?? anyc.google_sheet ?? anyc.googleSheetId ?? anyc.sheet_id ?? anyc.sheetId ?? null;
    const resolved = await resolveGoogleSheetName(sheetId);
    setDetailsGoogleSheetName(resolved);
    // resolver nombre de plantilla si campaign tiene template id (async)
    const rawTplId = anyc.template_id ?? anyc.template ?? anyc.templateId ?? null;
    const tplId = rawTplId ? String(rawTplId) : null;
    if (tplId) {
      const tplName = await resolveTemplateName(tplId);
      setDetailsTemplateName(tplName);
    } else {
      // si campaign trae directamente nombre de plantilla, usarlo.
      // NO usar como fallback el nombre de la campaña para evitar repetirlo.
      const rawTplName = anyc.whatsapp_template_name ?? anyc.template_name ?? null;
      setDetailsTemplateName(rawTplName ? formatTemplateName(rawTplName) : null);
    }

    // ✅ NUEVO: Cargar mensajes de la campaña
    await loadCampaignMessages(campaign.id);
  };

  const closeDetails = () => {
    setShowDetailsModal(false);
    setDetailsCampaign(null);
    setDetailsGoogleSheetName(null);
    setDetailsTemplateName(null);
    // ✅ NUEVO: Limpiar mensajes
    setCampaignMessages([]);
    setMessagesStatusCounts({});
    setMessagesTotal(0);
  };

  // ✅ NUEVO: Cargar mensajes de una campaña
  const loadCampaignMessages = async (campaignId: string) => {
    setMessagesLoading(true);
    try {
      const response = await api.get<CampaignMessagesResponse>(`/campaigns/${campaignId}/messages`);
      if (response.data.success) {
        setCampaignMessages(response.data.messages);
        setMessagesStatusCounts(response.data.status_counts);
        setMessagesTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error loading campaign messages:', error);
      // No mostrar error si la campaña simplemente no tiene mensajes
      setCampaignMessages([]);
      setMessagesStatusCounts({});
      setMessagesTotal(0);
    } finally {
      setMessagesLoading(false);
    }
  };

  // ✅ NUEVO: Ejecutar campaña (enviar mensajes de WhatsApp)
  const executeCampaign = async (campaignId: string) => {
    setExecutingCampaign(campaignId);
    try {
      toast.loading('Enviando mensajes de WhatsApp...', { id: 'execute-campaign' });

      const response = await api.post<{
        success: boolean;
        total: number;
        sent: number;
        failed: number;
        skipped: number;
        error?: string;
      }>(`/campaigns/${campaignId}/execute`);

      if (response.data.success) {
        const { total, sent, failed, skipped } = response.data;
        toast.success(
          `Campaña ejecutada: ${sent} enviados, ${failed} fallidos, ${skipped} omitidos de ${total} total`,
          { id: 'execute-campaign', duration: 5000 }
        );

        // Actualizar campaña en el estado local
        setCampaigns(prev => prev.map(c =>
          c.id === campaignId
            ? { ...c, status: 'completed' as CampaignStatus, messages_sent: sent }
            : c
        ));

        // Si el modal de detalles está abierto, recargar mensajes
        if (showDetailsModal && detailsCampaign?.id === campaignId) {
          await loadCampaignMessages(campaignId);
        }

        // Recargar campañas
        await loadCampaigns();
      } else {
        toast.error(response.data.error || 'Error al ejecutar la campaña', { id: 'execute-campaign' });
      }
    } catch (error: any) {
      console.error('Error executing campaign:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Error al ejecutar la campaña';
      toast.error(errorMessage, { id: 'execute-campaign' });
    } finally {
      setExecutingCampaign(null);
    }
  };

  // ✅ NUEVO: Obtener icono de estado de mensaje
  const getMessageStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'delivered':
        return <CheckCircle2 className="h-4 w-4 text-blue-500" />;
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'skipped':
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
      default:
        return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  // ✅ NUEVO: Obtener texto de estado de mensaje
  const getMessageStatusText = (status: string) => {
    switch (status) {
      case 'sent': return 'Enviado';
      case 'delivered': return 'Entregado';
      case 'pending': return 'Pendiente';
      case 'failed': return 'Error';
      case 'skipped': return 'Omitido';
      default: return status;
    }
  };

  // ✅ NUEVO: Cargar nombres de plantillas de todas las campañas al inicio
  const loadAllTemplateNames = async (campaignsList: CampaignListItem[]) => {
    const templateIds = new Set<string>();

    // Recolectar todos los template_ids únicos
    campaignsList.forEach((campaign) => {
      const anyc = campaign as any;
      const templateId = anyc.template_id ?? anyc.template ?? anyc.templateId;
      if (templateId && !templateCache[String(templateId)]) {
        templateIds.add(String(templateId));
      }
    });

    if (templateIds.size === 0) return;

    console.log(`📥 Cargando ${templateIds.size} plantillas...`);

    // Cargar todas las plantillas en paralelo
    const promises = Array.from(templateIds).map(async (templateId) => {
      try {
        const res = await api.get<{ id?: string; name?: string; template?: string; message?: string; category?: string; header_type?: string; header_text?: string; header_image_url?: string; footer_text?: string; buttons?: Array<{ type: string; text: string; url?: string; phone_number?: string }> }>(`/templates/${templateId}`);
        const name = res?.data?.name ?? null;
        const message = res?.data?.message ?? res?.data?.template ?? undefined;
        const category = res?.data?.category ?? undefined;

        return {
          id: templateId,
          data: {
            name: name ?? undefined,
            message,
            template: message,
            body_text: message ?? undefined,
            category,
            header_type: res?.data?.header_type,
            header_text: res?.data?.header_text,
            header_image_url: res?.data?.header_image_url,
            footer_text: res?.data?.footer_text,
            buttons: res?.data?.buttons,
          }
        };
      } catch (err) {
        console.error(`Error loading template ${templateId}:`, err);
        return {
          id: templateId,
          data: { name: undefined, message: undefined, category: undefined }
        };
      }
    });

    const results = await Promise.all(promises);

    // Actualizar cache con todos los resultados
    const newCache = { ...templateCache };
    results.forEach(({ id, data }) => {
      newCache[id] = data;
    });

    setTemplateCache(newCache);
    console.log('✅ Plantillas cargadas:', Object.keys(newCache).length);
  };

  const loadTemplatesMap = async (): Promise<void> => {
    try {
      let localTemplates: any[] = [];
      try {
        const localRes = await api.get('/templates');
        localTemplates = Array.isArray(localRes.data) ? localRes.data : localRes.data?.data || [];
      } catch (localErr) {
        console.warn('Could not fetch local templates:', localErr);
      }

      let metaTemplates: any[] = [];
      try {
        const metaRes = await api.get('/whatsapp/templates/meta/list');
        if (metaRes.data.success && metaRes.data.templates) {
          metaTemplates = metaRes.data.templates;
        }
      } catch (metaErr) {
        console.warn('Could not fetch Meta templates:', metaErr);
      }

      setTemplatesMap(buildTemplatesMap(localTemplates, metaTemplates));
    } catch (error) {
      console.error('Error loading templates map:', error);
    }
  };

  // ✅ MODIFICADO: Cargar campañas Y plantillas (UNA SOLA DECLARACIÓN)
  const loadCampaigns = async (): Promise<void> => {
    try {
      setLoading(true);
      const response = await api.get<CampaignListItem[]>('/campaigns');
      setCampaigns(response.data);

      // Cargar plantillas de las campañas
      await loadAllTemplateNames(response.data);
    } catch (error) {
      console.error('Error loading campaigns:', error);
      toast.error('Error al cargar las campañas');
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NUEVO: Cargar campañas solo una vez al inicio
  useEffect(() => {
    console.log('✅ CampaignList mounted, loading campaigns + sheets');
    loadCampaigns();
    loadGoogleSheets();
    loadTemplatesMap();
  }, []);

  // ✅ MODIFICADO: Solo resetear página cuando cambian filtros (sin recargar)
  useEffect(() => {
    if (campaigns.length > 0) {
      setCurrentPage(1);
    }
  }, [statusFilter, sheetFilter, dateFromFilter, dateToFilter, sortBy, sortOrder]);

  // ✅ NUEVO: Filtrado local en lugar de recargar desde el servidor
  const getFilteredCampaigns = (): CampaignListItem[] => {
    let filtered = [...campaigns];

    // Búsqueda por texto (nombre de campaña o plantilla)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c => {
        const anyc = c as any;
        const campaignName = (c.name || '').toLowerCase();
        const templateName = (anyc.template_name || '').toLowerCase();
        const templateMessage = (anyc.message_template || anyc.template || anyc.message || '').toLowerCase();

        // Buscar en cache de plantillas
        const templateId = anyc.template_id ?? anyc.template ?? anyc.templateId;
        const cached = templateId ? templateCache[String(templateId)] : undefined;
        const cachedName = (cached?.name || '').toLowerCase();
        const cachedMessage = (cached?.message || cached?.template || '').toLowerCase();

        return campaignName.includes(search) ||
          templateName.includes(search) ||
          templateMessage.includes(search) ||
          cachedName.includes(search) ||
          cachedMessage.includes(search);
      });
    }

    // Filtro por estado
    if (statusFilter) {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    // Filtro por Google Sheet
    if (sheetFilter) {
      filtered = filtered.filter(c => {
        const anyc = c as any;
        const sheetId = anyc.google_sheet_id ?? anyc.google_sheet ?? anyc.googleSheetId ?? anyc.sheet_id ?? anyc.sheetId;
        return String(sheetId) === String(sheetFilter);
      });
    }

    // Filtro por fecha desde
    if (dateFromFilter) {
      const fromDate = new Date(dateFromFilter);
      filtered = filtered.filter(c => new Date(c.created_at) >= fromDate);
    }

    // Filtro por fecha hasta
    if (dateToFilter) {
      const toDate = new Date(dateToFilter);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(c => new Date(c.created_at) <= toDate);
    }

    // Ordenamiento
    filtered.sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'messages_sent':
          aVal = a.messages_sent || 0;
          bVal = b.messages_sent || 0;
          break;
        case 'delivery_rate':
          aVal = a.delivery_rate || 0;
          bVal = b.delivery_rate || 0;
          break;
        case 'created_at':
        default:
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
      }

      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  };

  const loadGoogleSheets = async (): Promise<void> => {
    try {
      const response = await api.get<GoogleSheetWithCount[]>('/google-sheets');
      setGoogleSheets(response.data);
    } catch (error) {
      console.error('Error loading Google Sheets:', error);
    }
  };

  const clearFilters = (): void => {
    setSearchTerm('');
    setStatusFilter('');
    setSheetFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const handleSortChange = (newSortBy: string): void => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const updateCampaignStatus = async (campaignId: string, newStatus: CampaignStatus): Promise<void> => {
    try {
      await api.put(`/campaigns/${campaignId}/status`, { status: newStatus });
      setCampaigns(prev => prev.map(c => c.id === campaignId ? { ...c, status: newStatus } : c));
      setDetailsCampaign(prev => prev && prev.id === campaignId ? { ...prev, status: newStatus } : prev);
      toast.success(`Campaña ${newStatus === 'active' ? 'activada' : newStatus === 'paused' ? 'pausada' : 'actualizada'} correctamente`);
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast.error('Error al actualizar la campaña');
    }
  };

  const deleteCampaign = async (campaignId: string): Promise<void> => {
    try {
      await api.delete(`/campaigns/${campaignId}`);
      setCampaigns(prev => prev.filter(c => c.id !== campaignId));
      toast.success('Campaña eliminada correctamente');
    } catch (error) {
      console.error('Error hiding campaign:', error);
      toast.error('No se pudo eliminar la campaña');
    } finally {
      closeDeleteConfirm();
    }
  };

  const getStatusBadge = (status: CampaignStatus) => {
    const statusConfig: Record<CampaignStatus, StatusConfig> = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Borrador' },
      active: { color: 'bg-green-100 text-green-700', label: 'Activa' },
      paused: { color: 'bg-yellow-100 text-yellow-700', label: 'Pausada' },
      completed: { color: 'bg-blue-100 text-blue-700', label: 'Completada' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <Badge variant="outline" className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const getStatusIcon = (status: CampaignStatus): string => {
    const icons: Record<CampaignStatus, string> = {
      draft: '📝',
      active: '🚀',
      paused: '⏸️',
      completed: '✅'
    };
    return icons[status] || '📝';
  };

  const CampaignCard: React.FC<CampaignCardProps> = ({ campaign }) => {
    const anyc = campaign as any;
    const rawTemplateId = anyc.template_id ?? anyc.template ?? anyc.templateId ?? undefined;
    const templateId = rawTemplateId ? String(rawTemplateId) : undefined;

    const templateNameFromCampaign = anyc.template_name ?? anyc.whatsapp_template_name ?? anyc.meta_template_name ?? undefined;
    const cached = templateId ? templateCache[templateId] : undefined;

    let templatePreview = resolveTemplatePreview({
      templateName: templateNameFromCampaign,
      templateId,
      messageTemplateFallback:
        anyc.message_template ?? anyc.template ?? anyc.message ?? anyc.template_text ?? anyc.content,
      templatesMap,
      templateCache,
      campaignFields: {
        name: cached?.name,
        header_type: anyc.header_type,
        header_text: anyc.header_text,
        header_image_url: anyc.header_image_url,
        footer_text: anyc.footer_text,
        buttons: anyc.buttons,
        ...cached,
      },
    });

    if (templatePreview.name && String(templatePreview.name) === String(campaign.name)) {
      const altName = cached?.name && String(cached.name) !== String(campaign.name) ? formatTemplateName(cached.name) : null;
      templatePreview = { ...templatePreview, name: altName ?? templatePreview.name };
    }
    if (!templatePreview.name) {
      templatePreview.name = formatTemplateName(templateNameFromCampaign ?? cached?.name);
    }

    const isTemplateLoading = !!templateId && !cached && !templatePreview.bodyText;

    return (
      <Card className="hover:shadow-lg transition-all duration-300 border-gray-200">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg flex items-center justify-center">
                <span className="text-xl">{getStatusIcon(campaign.status)}</span>
              </div>
              <div>
                <CardTitle className="text-lg">{campaign.name}</CardTitle>
                <CardDescription className="space-y-1">
                  <div>Creada el {new Date(campaign.created_at).toLocaleDateString()}</div>
                  {campaign.google_sheet_name && (
                    <div className="flex items-center space-x-1 text-blue-600">
                      <FileText className="h-3 w-3" />
                      <span className="text-xs">{campaign.google_sheet_name}</span>
                    </div>
                  )}
                </CardDescription>
              </div>
            </div>
            {getStatusBadge(campaign.status)}
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            {/* Campaign Stats */}
            <div className="grid grid-cols-3 gap-4 text-center">
              <div className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="text-2xl font-bold text-gray-900">{campaign.messages_sent || 0}</div>
                <div className="text-xs text-gray-600">Enviados</div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="text-2xl font-bold text-gray-900">{campaign.messages_delivered || 0}</div>
                <div className="text-xs text-gray-600">Entregados</div>
              </div>
              <div className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="text-2xl font-bold text-gray-900">{(campaign.delivery_rate || 0).toFixed(1)}%</div>
                <div className="text-xs text-gray-600">Tasa entrega</div>
              </div>
            </div>

            <TemplatePreviewPanel
              preview={templatePreview}
              showTemplateName={true}
              loading={isTemplateLoading}
            />

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-2 pt-2">
              {(() => {
                const btnBase = "h-10 px-3 flex items-center justify-center rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 border";
                const isExecuting = executingCampaign === campaign.id;
                return (
                  <>
                    {/* Primera fila: Botones de Estado */}
                    {campaign.status === 'draft' && (
                      <Button
                        size="sm"
                        className={`${btnBase} bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:border-green-300 shadow-sm hover:shadow`}
                        onClick={() => executeCampaign(campaign.id)}
                        disabled={isExecuting}
                      >
                        {isExecuting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-1.5" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-1.5" /> Enviar
                          </>
                        )}
                      </Button>
                    )}

                    {campaign.status === 'active' && (
                      <Button
                        size="sm"
                        className={`${btnBase} bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 hover:border-amber-300 shadow-sm hover:shadow`}
                        onClick={() => updateCampaignStatus(campaign.id, 'paused')}
                      >
                        <span className="mr-1.5">⏸️</span> Pausar
                      </Button>
                    )}

                    {campaign.status === 'paused' && (
                      <Button
                        size="sm"
                        className={`${btnBase} bg-green-50 text-green-700 border-green-200 hover:bg-green-100 hover:border-green-300 shadow-sm hover:shadow`}
                        onClick={() => executeCampaign(campaign.id)}
                        disabled={isExecuting}
                      >
                        {isExecuting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-1.5" />
                            Enviando...
                          </>
                        ) : (
                          <>
                            <Send className="h-4 w-4 mr-1.5" /> Reenviar
                          </>
                        )}
                      </Button>
                    )}

                    {/* Botón Detalles - Siempre visible */}
                    <Button
                      size="sm"
                      className={`${btnBase} bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 hover:border-blue-300 shadow-sm hover:shadow`}
                      onClick={() => openDetails(campaign)}
                      aria-label="Ver detalles de campaña"
                    >
                      <span className="mr-1.5">📊</span> Detalles
                    </Button>

                    {/* Botón Eliminar */}
                    <Button
                      size="sm"
                      className={`${btnBase} bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 hover:border-rose-300 shadow-sm hover:shadow`}
                      onClick={() => openDeleteConfirm(campaign)}
                    >
                      <span className="mr-1.5">🗑️</span> Eliminar
                    </Button>
                  </>
                );
              })()}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const sortOptions: SortOption[] = [
    { key: 'created_at', label: 'Fecha de creación' },
    { key: 'name', label: 'Nombre' },
    { key: 'messages_sent', label: 'Mensajes enviados' },
    { key: 'delivery_rate', label: '% Entregados' }
  ];

  const hasActiveFilters = searchTerm || statusFilter || sheetFilter || dateFromFilter || dateToFilter || sortBy !== 'created_at' || sortOrder !== 'desc';

  // ✅ MODIFICADO: Obtener campañas filtradas y paginadas
  const filteredCampaigns = getFilteredCampaigns();
  const totalPages = Math.max(1, Math.ceil(filteredCampaigns.length / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCampaigns = filteredCampaigns.slice(startIndex, endIndex);

  // ✅ MODIFICADO: Resetear página cuando cambian los resultados filtrados
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [filteredCampaigns.length, currentPage, totalPages]);

  // ✅ MODIFICADO: Funciones de navegación de páginas (mismo estilo que MessageTemplates)
  const goToPage = (page: number) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goToPreviousPage = () => goToPage(currentPage - 1);
  const goToNextPage = () => goToPage(currentPage + 1);

  if (loading) {
    return (
      <div className="animate-fadeIn">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Cargando campañas...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="text-center sm:text-left">
          <h1 className="text-3xl font-bold text-gray-900">Campañas</h1>
          <p className="text-gray-600 mt-1">Gestiona tus campañas de WhatsApp</p>
        </div>
        <div className="flex justify-center sm:justify-end">
          <Button
            onClick={onCreateNew}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2"
          >
            <span className="text-xl">🚀</span> Nueva Campaña
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <Card className="border-gray-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar por nombre o texto de plantilla..."
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // ✅ Resetear página al buscar
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center space-x-2"
              >
                <Filter className="h-4 w-4" />
                <span>Filtros</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </Button>

              {hasActiveFilters && (
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="flex items-center space-x-2 text-red-600 hover:text-red-700"
                >
                  <RotateCcw className="h-4 w-4" />
                  <span>Limpiar</span>
                </Button>
              )}
            </div>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
                  <select
                    value={statusFilter}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todos los estados</option>
                    <option value="draft">Borrador</option>
                    <option value="active">Activa</option>
                    <option value="paused">Pausada</option>
                    <option value="completed">Completada</option>
                  </select>
                </div>

                {/* Google Sheet Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hoja de Google</label>
                  <select
                    value={sheetFilter}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSheetFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Todas las hojas</option>
                    {googleSheets.map((sheet) => (
                      <option key={sheet.id} value={sheet.id}>
                        {sheet.name} ({sheet.campaign_count})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Date From Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Desde</label>
                  <input
                    type="date"
                    value={dateFromFilter}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateFromFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Date To Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hasta</label>
                  <input
                    type="date"
                    value={dateToFilter}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDateToFilter(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Sort Options */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Ordenar por:</label>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((option) => (
                    <Button
                      key={option.key}
                      variant={sortBy === option.key ? "default" : "outline"}
                      size="sm"
                      onClick={() => handleSortChange(option.key)}
                      className="flex items-center space-x-1"
                    >
                      <span>{option.label}</span>
                      {sortBy === option.key && (
                        sortOrder === 'asc' ? <SortAsc className="h-3 w-3" /> : <SortDesc className="h-3 w-3" />
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Campaign Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {(() => {
          const total = campaigns.length;
          const activeCount = campaigns.filter(c => c.status === 'active').length;
          const draftCount = campaigns.filter(c => c.status === 'draft').length;
          const completedCount = campaigns.filter(c => c.status === 'completed').length;

          return (
            <>
              <Card
                className={`cursor-pointer hover:shadow-lg transition ${!statusFilter ? 'ring-2 ring-blue-200' : ''}`}
                onClick={() => { setStatusFilter(''); setSearchTerm(''); setCurrentPage(1); }}
                aria-label="Filtrar todas las campañas"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total</p>
                      <p className="text-2xl font-bold">{total}</p>
                    </div>
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span>📢</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'active' ? 'ring-2 ring-green-200' : ''}`}
                onClick={() => { setStatusFilter('active'); setSearchTerm(''); setCurrentPage(1); }}
                aria-label="Filtrar campañas activas"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Activas</p>
                      <p className="text-2xl font-bold text-green-600">{activeCount}</p>
                    </div>
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <span>🚀</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'draft' ? 'ring-2 ring-yellow-200' : ''}`}
                onClick={() => { setStatusFilter('draft'); setSearchTerm(''); setCurrentPage(1); }}
                aria-label="Filtrar borradores"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Borradores</p>
                      <p className="text-2xl font-bold text-yellow-600">{draftCount}</p>
                    </div>
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <span>📝</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`cursor-pointer hover:shadow-lg transition ${statusFilter === 'completed' ? 'ring-2 ring-blue-200' : ''}`}
                onClick={() => { setStatusFilter('completed'); setSearchTerm(''); setCurrentPage(1); }}
                aria-label="Filtrar completadas"
              >
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Completadas</p>
                      <p className="text-2xl font-bold text-blue-600">{completedCount}</p>
                    </div>
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <span>✅</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          );
        })()}
      </div>

      {/* ✅ MODIFICADO: Campaigns List usando filteredCampaigns */}
      {filteredCampaigns.length === 0 ? (
        <Card className="border-2 border-dashed border-gray-200">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl text-white">
                  {hasActiveFilters ? '🔍' : '📢'}
                </span>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {hasActiveFilters
                  ? 'No se encontraron campañas'
                  : 'No hay campañas aún'
                }
              </h3>
              <p className="text-gray-500 mb-6">
                {hasActiveFilters
                  ? 'Intenta ajustar los filtros de búsqueda para encontrar las campañas que buscas'
                  : 'Crea tu primera campaña para comenzar a enviar mensajes automatizados'
                }
              </p>
              {hasActiveFilters ? (
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="mr-3"
                >
                  🔄 Limpiar filtros
                </Button>
              ) : null}
              <Button
                onClick={onCreateNew}
                className="bg-gradient-to-r from-blue-600 to-purple-600"
              >
                🚀 {campaigns.length === 0 && !hasActiveFilters ? 'Crear Primera Campaña' : 'Nueva Campaña'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedCampaigns.map((campaign) => (
              <CampaignCard key={campaign.id} campaign={campaign} />
            ))}
          </div>

          {totalPages > 1 && (
            <Card className="border-gray-200">
              <CardContent className="pt-6">
                {/* ✅ MODIFICADO: Paginación con el mismo diseño que MessageTemplates */}
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Página {currentPage} de {totalPages} — mostrando {paginatedCampaigns.length} de {filteredCampaigns.length}
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                    >
                      Anterior
                    </Button>

                    {/* Páginas numeradas */}
                    <div className="hidden sm:flex items-center gap-1">
                      {Array.from({ length: totalPages }).map((_, i) => {
                        const page = i + 1;
                        const isActive = page === currentPage;
                        return (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`px-3 py-1 rounded-md text-sm ${isActive
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                    </div>

                    <Button
                      size="sm"
                      variant="outline"
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                    >
                      Siguiente
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Modal de Edición (al final, antes del cierre del div principal) */}
      {showEditModal && editingCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-6">
          <div
            className="bg-white rounded-xl shadow-2xl w-full mx-4 overflow-hidden"
            style={{ maxWidth: '95vw', maxHeight: '90vh' }}
          >
            {/* Header del Modal */}
            <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-50 to-purple-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">✏️ Editar Campaña</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Modifica los detalles de: <span className="font-semibold">{editingCampaign.name}</span>
                </p>
              </div>
              <Button
                variant="ghost"
                onClick={closeEditModal}
                className="text-gray-500 hover:text-gray-700"
              >
                ✖️
              </Button>
            </div>

            {/* Contenido del Modal con Scroll */}
            <div
              className="overflow-y-auto p-6"
              style={{ maxHeight: 'calc(90vh - 80px)' }}
            >
              <CampaignBuilder
                onBack={closeEditModal}
                editingCampaign={editingCampaign}
              />
            </div>
          </div>
        </div>
      )}

      {/* Campaign Details Modal */}
      {showDetailsModal && detailsCampaign && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 p-6">
          <div
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full mx-4 overflow-hidden"
            style={{ maxHeight: '80vh' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Detalles de la Campaña</h2>
                <p className="text-sm text-gray-500">Información de campaña y plantilla asociada</p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  onClick={closeDetails}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✖️
                </Button>
              </div>
            </div>

            {/* Content: scroll interno si el contenido excede la altura máxima del modal */}
            <div
              className="p-6 space-y-6 overflow-y-auto"
              style={{ maxHeight: 'calc(80vh - 96px)' }}
            >
              {/* Top: Campaign summary */}
              {(() => {
                const anyc = detailsCampaign as any;
                const sendCandidates = anyc.send_at ?? anyc.sendAt ?? anyc.sendDate ?? anyc.scheduled_at ?? anyc.scheduledAt ?? anyc.sendDateTime ?? null;
                const sendDate = sendCandidates ? new Date(sendCandidates) : null;
                const estimated = anyc.estimated_messages ?? anyc.estimatedMessages ?? anyc.estimated ?? 0;
                const sheetId = anyc.google_sheet_id ?? anyc.google_sheet ?? anyc.googleSheetId ?? anyc.sheet_id ?? anyc.sheetId ?? null;
                const phoneColumn = anyc.phone_column ?? anyc.phoneColumn ?? anyc.phoneColumnName ?? anyc.phoneColumnSelected ?? 'Automático';

                const sheetFromState = sheetId ? googleSheets.find(s => {
                  const candidates = [(s as any).id, (s as any).sheet_id, (s as any)._id, (s as any).sheetId];
                  return candidates.some(c => c != null && String(c) === String(sheetId));
                }) : undefined;
                const googleSheetNameResolved = detailsCampaign.google_sheet_name ?? sheetFromState?.name ?? (sheetId ? String(sheetId) : '—');

                return (
                  <div className="bg-white border rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <div className="text-xs text-gray-400 uppercase tracking-wide">Nombre de la campaña</div>
                        <div className="mt-1 text-2xl font-bold text-gray-900">{detailsCampaign.name || '—'}</div>

                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <div>
                            <div className="text-xs text-gray-400">Fecha de envío</div>
                            <div className="text-sm font-medium text-gray-800">
                              {sendDate ? sendDate.toLocaleString() : 'No programada'}
                            </div>
                          </div>

                          <div>
                            <div className="text-xs text-gray-400">Envíos estimados</div>
                            <div className="text-sm font-medium text-gray-800">{Number(estimated) ?? 0} mensajes</div>
                          </div>

                          <div>
                            <div className="text-xs text-gray-400">Hoja de datos</div>
                            <div className="text-sm font-medium text-gray-800">{googleSheetNameResolved}</div>
                          </div>

                          <div>
                            <div className="text-xs text-gray-400">Columna teléfono</div>
                            <div className="text-sm font-medium text-gray-800">{phoneColumn || 'Automático'}</div>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end justify-start">
                        <div className="text-xs text-gray-400 uppercase tracking-wide">Estado</div>
                        <div className="mt-2">{getStatusBadge(detailsCampaign.status)}</div>
                        <div className="mt-4 text-xs text-gray-400">Creada</div>
                        <div className="text-sm text-gray-700">{detailsCampaign.created_at ? new Date(detailsCampaign.created_at).toLocaleString() : '—'}</div>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Divider */}
              <div className="h-px bg-gray-100" />

              {/* Bottom: Template details */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Plantilla asociada</div>
                    {(() => {
                      const anyc = detailsCampaign as any;
                      const rawTemplateId = anyc.template_id ?? anyc.template ?? anyc.templateId ?? undefined;
                      const tplId = rawTemplateId ? String(rawTemplateId) : undefined;
                      const cached = tplId ? templateCache[tplId] : undefined;

                      const templateNameRaw =
                        detailsTemplateName ??
                        anyc.whatsapp_template_name ??
                        anyc.meta_template_name ??
                        anyc.template_name ??
                        cached?.name;
                      const templateType =
                        (anyc.template_type ?? anyc.templateType ?? (cached as any)?.category) ?? 'N/A';

                      const templatePreview = resolveTemplatePreview({
                        templateName: templateNameRaw,
                        templateId: tplId,
                        messageTemplateFallback:
                          anyc.message_template ?? anyc.template ?? anyc.message ?? anyc.template_text ?? anyc.content,
                        templatesMap,
                        templateCache,
                        campaignFields: {
                          ...cached,
                          header_type: anyc.header_type,
                          header_text: anyc.header_text,
                          header_image_url: anyc.header_image_url,
                          footer_text: anyc.footer_text,
                          buttons: anyc.buttons,
                        },
                      });

                      if (!templatePreview.name) {
                        templatePreview.name = templateNameRaw
                          ? formatTemplateName(templateNameRaw)
                          : 'Sin nombre de plantilla';
                      }

                      return (
                        <>
                          <div className="mt-1 text-lg font-semibold text-gray-900">{templatePreview.name}</div>
                          <div className="mt-2 text-sm text-gray-600">
                            <span className="text-xs text-gray-400 uppercase">Tipo:</span>{' '}
                            <span className="font-medium text-gray-800 ml-2">{templateType}</span>
                          </div>

                          <div className="mt-4 max-w-lg">
                            <div className="text-xs text-gray-400 uppercase tracking-wide">Vista previa del mensaje</div>
                            <div className="mt-2 bg-[#FAFBFC] border border-gray-200 rounded-xl p-4 overflow-y-auto max-h-64 shadow-sm">
                              <TemplatePreviewPanel
                                preview={templatePreview}
                                showTemplateName={false}
                                showSectionLabel={false}
                                embedded
                                emptyMessage="No hay mensaje disponible."
                              />
                            </div>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* ID de plantilla y sección de variables eliminadas intencionadamente */}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-gray-100" />

              {/* WhatsApp Meta Template Info */}
              {(() => {
                const anyc = detailsCampaign as any;
                const waTemplateName = anyc.whatsapp_template_name;
                const waTemplateLanguage = anyc.whatsapp_template_language || 'es_MX';

                if (!waTemplateName) return null;

                return (
                  <>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <div className="text-2xl">📱</div>
                        <div className="flex-1">
                          <div className="text-xs text-green-600 uppercase tracking-wide font-medium">
                            Plantilla de WhatsApp (Meta)
                          </div>
                          <div className="mt-1 text-lg font-semibold text-gray-900">
                            {waTemplateName}
                          </div>
                          <div className="mt-1 text-sm text-gray-600">
                            Idioma: <span className="font-medium">{waTemplateLanguage}</span>
                          </div>
                          <div className="mt-2 flex items-center gap-2 text-xs text-green-600">
                            <span className="inline-flex items-center gap-1 bg-green-100 px-2 py-0.5 rounded-full">
                              ✓ Aprobada por Meta
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="h-px bg-gray-100" />
                  </>
                );
              })()}

              {/* ✅ NUEVO: Sección de Estado de Envíos */}
              <div className="bg-white border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-xs text-gray-400 uppercase tracking-wide">Estado de Envíos</div>
                    <div className="mt-1 text-lg font-semibold text-gray-900">
                      {messagesTotal > 0 ? `${messagesTotal} destinatarios` : 'Sin envíos aún'}
                    </div>
                  </div>

                  {/* Status summary badges */}
                  {messagesTotal > 0 && (
                    <div className="flex gap-2">
                      {messagesStatusCounts.sent > 0 && (
                        <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                          ✓ {messagesStatusCounts.sent} enviados
                        </Badge>
                      )}
                      {messagesStatusCounts.failed > 0 && (
                        <Badge variant="outline" className="bg-red-100 text-red-700 border-red-200">
                          ✗ {messagesStatusCounts.failed} fallidos
                        </Badge>
                      )}
                      {messagesStatusCounts.pending > 0 && (
                        <Badge variant="outline" className="bg-yellow-100 text-yellow-700 border-yellow-200">
                          ⏳ {messagesStatusCounts.pending} pendientes
                        </Badge>
                      )}
                      {messagesStatusCounts.skipped > 0 && (
                        <Badge variant="outline" className="bg-gray-100 text-gray-600 border-gray-200">
                          ⊘ {messagesStatusCounts.skipped} omitidos
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* Messages list */}
                {messagesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mr-2" />
                    <span className="text-gray-500">Cargando envíos...</span>
                  </div>
                ) : campaignMessages.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {/* Table header */}
                    <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 rounded-lg text-xs font-medium text-gray-500 uppercase">
                      <div className="col-span-1">#</div>
                      <div className="col-span-3">Nombre</div>
                      <div className="col-span-3">Teléfono</div>
                      <div className="col-span-2">Estado</div>
                      <div className="col-span-3">Detalle</div>
                    </div>

                    {/* Message rows */}
                    {campaignMessages.map((msg, index) => (
                      <div
                        key={msg.id}
                        className={`grid grid-cols-12 gap-2 px-3 py-2 rounded-lg text-sm ${msg.status === 'failed' ? 'bg-red-50' :
                          msg.status === 'sent' || msg.status === 'delivered' ? 'bg-green-50' :
                            'bg-gray-50'
                          }`}
                      >
                        <div className="col-span-1 text-gray-400">{msg.row_index || index + 1}</div>
                        <div className="col-span-3 font-medium text-gray-900 truncate">
                          {msg.name || '—'}
                        </div>
                        <div className="col-span-3 text-gray-600 font-mono text-xs">
                          {msg.phone_number}
                        </div>
                        <div className="col-span-2 flex items-center gap-1">
                          {getMessageStatusIcon(msg.status)}
                          <span className={`text-xs ${msg.status === 'sent' || msg.status === 'delivered' ? 'text-green-600' :
                            msg.status === 'failed' ? 'text-red-600' :
                              msg.status === 'pending' ? 'text-yellow-600' :
                                'text-gray-500'
                            }`}>
                            {getMessageStatusText(msg.status)}
                          </span>
                        </div>
                        <div className="col-span-3 text-xs text-gray-500 truncate" title={msg.error || msg.sent_at || ''}>
                          {msg.error ? (
                            <span className="text-red-600">{msg.error}</span>
                          ) : msg.sent_at ? (
                            <span>Enviado: {new Date(msg.sent_at).toLocaleTimeString()}</span>
                          ) : (
                            '—'
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <AlertCircle className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>No se han enviado mensajes aún.</p>
                    <p className="text-sm mt-1">Haz clic en "Enviar" para iniciar la campaña.</p>
                  </div>
                )}

                {/* Action button to execute campaign from details modal */}
                {detailsCampaign && (detailsCampaign.status === 'draft' || detailsCampaign.status === 'paused') && (
                  <div className="mt-4 pt-4 border-t">
                    <Button
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                      onClick={() => executeCampaign(detailsCampaign.id)}
                      disabled={executingCampaign === detailsCampaign.id}
                    >
                      {executingCampaign === detailsCampaign.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Enviando mensajes...
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4 mr-2" />
                          Enviar Campaña Ahora
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Refresh button */}
                {messagesTotal > 0 && (
                  <div className="mt-4 pt-4 border-t flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadCampaignMessages(detailsCampaign.id)}
                      disabled={messagesLoading}
                    >
                      <RotateCcw className={`h-4 w-4 mr-1 ${messagesLoading ? 'animate-spin' : ''}`} />
                      Actualizar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full overflow-hidden">
            <div className="p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                  <span className="text-red-600 text-lg">🗑️</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Eliminar campaña</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Esta campaña dejará de mostrarse en tus listas. ¿Deseas continuar?
                  </p>
                </div>
              </div>
            </div>

            <div className="px-4 pb-4">
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={closeDeleteConfirm} className="px-4">
                  Cancelar
                </Button>
                <Button
                  className="bg-red-600 hover:bg-red-700 text-white px-4"
                  onClick={() => deleteCampaign(deleteTarget.id)}
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignList;
